import { ReactNode, useMemo, useRef, useState } from "react";

import { EventClickArg, EventContentArg, EventInput } from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import { Box } from "@mui/material";
import { isFromNestedInteractiveElement } from "@shared";

interface Props<T> {
  events: T[];
  mapEvent: (event: T) => EventInput;
  renderEvent: (event: T) => ReactNode;
  onEventClick: (event: T) => void;
}

export function useCalendar<T>({ events, renderEvent, onEventClick, mapEvent }: Props<T>) {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const calendarEvents: EventInput[] = useMemo(() => events.map((event) => mapEvent(event)), [events, mapEvent]);

  const updateCurrentDate = () => {
    const date = calendarRef.current?.getApi().getDate();
    if (date) {
      setCurrentDate(date);
    }
  };

  const goNext = () => {
    calendarRef.current?.getApi().next();
    updateCurrentDate();
  };

  const goPrev = () => {
    calendarRef.current?.getApi().prev();
    updateCurrentDate();
  };

  const goToday = () => {
    calendarRef.current?.getApi().today();
    updateCurrentDate();
  };

  // A plain wrapper, not a button: the event's links and buttons must not be nested inside one.
  // FullCalendar's eventClick opens the event on a mouse click; for the keyboard, every renderEvent
  // carries its own RowActionButton.
  const handleEventContent = (arg: EventContentArg) => {
    if (arg.event.display === "background") {
      return null;
    }

    const originalEvent = arg.event.extendedProps.originalEvent as T;

    return (
      <Box maxWidth="100%" height="100%">
        {renderEvent(originalEvent)}
      </Box>
    );
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    // FullCalendar listens natively on the calendar element, so it sees a click on a link inside the
    // event before React runs the link's stopPropagation; without this guard the preventDefault below
    // cancels the link's navigation and the event opens instead.
    if (isFromNestedInteractiveElement(clickInfo.jsEvent.target, clickInfo.el)) {
      return;
    }

    clickInfo.jsEvent.preventDefault();
    const originalEvent = clickInfo.event.extendedProps.originalEvent as T;
    onEventClick(originalEvent);
  };

  return {
    calendarRef,
    currentDate,
    goNext,
    goPrev,
    goToday,
    calendarEvents,
    handleEventContent,
    handleEventClick,
  };
}
